import { useState, useEffect, useRef, useCallback } from 'react';
import type { WidgetSizeClass, WidgetBreakpoints } from './widgets/types';

const DEFAULT_BREAKPOINTS: WidgetBreakpoints = { compact: 300, medium: 500 };
const DEBOUNCE_MS = 150;

export interface WidgetSizeInfo {
  pixelSize: { width: number; height: number };
  sizeClass: WidgetSizeClass;
}

function classifySize(width: number, breakpoints: WidgetBreakpoints): WidgetSizeClass {
  if (width <= breakpoints.compact) return 'compact';
  if (width <= breakpoints.medium) return 'medium';
  return 'full';
}

export function useWidgetSize(
  breakpoints?: WidgetBreakpoints,
): { ref: React.RefObject<HTMLDivElement | null>; sizeInfo: WidgetSizeInfo } {
  const ref = useRef<HTMLDivElement | null>(null);
  const bp = breakpoints ?? DEFAULT_BREAKPOINTS;

  const [sizeInfo, setSizeInfo] = useState<WidgetSizeInfo>({
    pixelSize: { width: 0, height: 0 },
    sizeClass: 'full',
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (!entry) return;

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(() => {
        const { width, height } = entry.contentRect;
        setSizeInfo({
          pixelSize: { width, height },
          sizeClass: classifySize(width, bp),
        });
      }, DEBOUNCE_MS);
    },
    [bp],
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new ResizeObserver(handleResize);
    observer.observe(el);

    const rect = el.getBoundingClientRect();
    setSizeInfo({
      pixelSize: { width: rect.width, height: rect.height },
      sizeClass: classifySize(rect.width, bp),
    });

    return () => {
      observer.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [handleResize, bp]);

  return { ref, sizeInfo };
}
